/**
 * A saved interview's Review tab, laid out as Linear's issue view in one
 * centred column: the title, summary and a row of properties and rated areas
 * pinned at the top; under them the moments and one list of actions,
 * scrolling; and docked at the foot the platform's own chat composer, as in
 * Chat, that asks Amoreg about this interview. The view model is
 * `ProbeReview.logic.ts`.
 *
 * @module components/apps/probe/ProbeReview
 */
import type { ProbeExaminationArea } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import { CalendarIcon, ClockIcon, MessageSquareTextIcon, RotateCwIcon, TargetIcon } from "lucide-react";
import { useId, useRef, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { areaLabel } from "@amoreg/shared/probePractice";
import { HomeComposer, type HomeComposerApi, type HomeComposerProject } from "../../home/HomeComposer";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../../ui/collapsible";
import { StatusIcon } from "../../regulation/finding/findingGlyphs";
import type { DeskRecord } from "./desk/deskRecords";
import { setActionDone, useActionsDone } from "./probeActionsDone";
import { areaSlug } from "./probeArea";
import { AssessmentGlyph, OutcomeStatus, ProbeProperty, QUIET } from "./ProbeParts";
import { ProbeRecordChips } from "./ProbeRecordChips";
import {
  recordsInPlay,
  type RatedArea,
  type ReviewArea,
  type ReviewMoment,
  type ReviewPage,
} from "./ProbeReview.logic";

/** Asking Amoreg about the interview: the org's chat, and the interview named around what was typed. */
export interface ProbeReviewAsk {
  readonly project: HomeComposerProject;
  /** The message sent for what the person typed: the interview by id, then their question. */
  readonly frame: (question: string) => string;
}

export interface ProbeReviewProps {
  readonly interviewId: string;
  /** The interview's name: the page's title. */
  readonly title: string;
  /** When it was held, and how long it ran (null when it was not kept). */
  readonly facts: { readonly date: string; readonly length: string | null };
  readonly page: ReviewPage;
  readonly orgSegment: string;
  /** The records under each examiner line (`lineRecordsOf`), for what was in play at each moment. */
  readonly lineRecords: ReadonlyMap<string, ReadonlyArray<DeskRecord>>;
  /** Start a call on an area, or a plain one for null. */
  readonly onRehearse: (area: ProbeExaminationArea | null) => void;
  /** The transcript, scrolled to this line and marked. */
  readonly onTranscript: (turnKey: string) => void;
  /** Null where the organization has no chat to ask in. */
  readonly ask: ProbeReviewAsk | null;
  /** Ask for a failed review again; absent where it cannot be asked for. */
  readonly retry?: { readonly run: () => void; readonly busy: boolean };
}

/** What stands where the moments go: the moments, or the debrief's areas on an interview with no review. */
const findingsTitle = (body: ReviewPage["body"]) =>
  body.kind === "debrief" ? "To work on" : "Moments";
const findingsCount = (body: ReviewPage["body"]) =>
  body.kind === "ready" ? body.moments.length : body.kind === "debrief" ? body.areas.length : null;

/** Chat's composer column: its inset and its 48rem, so the page and the composer share edges. */
const INSET = "chat-composer-horizontal-inset w-full";
const COLUMN = "mx-auto w-full max-w-3xl";

/** The question a moment's Ask puts in the composer. */
export const momentQuestion = (moment: ReviewMoment): string =>
  `Why did my answer on ${areaLabel(moment.area)} fall short, and what evidence would make it strong?`;

export function ProbeReview({
  interviewId,
  title,
  facts,
  page,
  orgSegment,
  lineRecords,
  onRehearse,
  onTranscript,
  ask,
  retry,
}: ProbeReviewProps) {
  const { verdict, body, areas, actions } = page;
  const composer = useRef<HomeComposerApi | null>(null);
  // On an interview with no review, the areas to work on are the body: not shown twice.
  const showAreas = body.kind !== "debrief" && areas.length > 0;
  return (
    <div
      className="@container/review flex min-h-0 flex-1 flex-col overflow-hidden"
      data-probe-review={body.kind}
    >
      {/* Wide, the header is pinned and the column scrolls; on a phone both scroll, so the moments get the room. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto @2xl/review:overflow-hidden">
        <header className={cn(INSET, "shrink-0 border-b border-border/60")} data-probe-review-header="">
          <div className={cn(COLUMN, "flex flex-col gap-2 pt-6 pb-4 sm:pt-7")}>
            <h1 className="text-xl leading-7 font-semibold tracking-[-0.01em] text-foreground text-balance">
              {title}
            </h1>
            {verdict.summary !== null ? (
              <p
                className="line-clamp-3 text-sm leading-6 text-pretty text-foreground/85 @3xl/review:line-clamp-none"
                data-probe-summary=""
              >
                {verdict.summary}
              </p>
            ) : null}
            <div
              aria-label="Properties"
              role="group"
              className="-ms-2 flex flex-wrap items-center gap-x-1 gap-y-1.5 text-[13px]"
              data-probe-properties=""
            >
              {verdict.outcome !== null ? (
                <ProbeProperty>
                  <OutcomeStatus outcome={verdict.outcome} />
                </ProbeProperty>
              ) : null}
              <ProbeProperty icon={<CalendarIcon />}>{facts.date}</ProbeProperty>
              {facts.length !== null ? <ProbeProperty icon={<ClockIcon />}>{facts.length}</ProbeProperty> : null}
              {showAreas ? <Areas areas={areas} orgSegment={orgSegment} /> : null}
            </div>
          </div>
        </header>
        <div className={cn(INSET, "@2xl/review:min-h-0 @2xl/review:flex-1 @2xl/review:overflow-y-auto")} data-probe-review-main="">
          <div className={cn(COLUMN, "flex flex-col gap-5 pt-5 pb-6")}>
            <ReviewGroup title={findingsTitle(body)} meta={findingsCount(body)}>
              <Findings
                body={body}
                orgSegment={orgSegment}
                lineRecords={lineRecords}
                onRehearse={onRehearse}
                onTranscript={onTranscript}
                onAsk={ask === null ? null : (question) => composer.current?.prefill(question)}
                retry={retry}
              />
            </ReviewGroup>
            <Actions interviewId={interviewId} actions={actions} />
          </div>
        </div>
      </div>
      {ask !== null ? (
        <div className={cn(INSET, "shrink-0 pt-1.5 sm:pt-2")} data-probe-review-ask="">
          <HomeComposer
            project={ask.project}
            placeholder="Ask about this interview…"
            framePrompt={ask.frame}
            apiRef={composer}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Linear's disclosure group, as its sub-issues sit: a caret, the title and a count, rows under a hairline. */
function ReviewGroup({
  title,
  meta,
  dataAttribute,
  children,
}: {
  readonly title: string;
  readonly meta: ReactNode;
  readonly dataAttribute?: string;
  readonly children: ReactNode;
}) {
  return (
    <Collapsible
      defaultOpen
      render={<section aria-label={title} />}
      className="flex flex-col"
      {...(dataAttribute === undefined ? {} : { [dataAttribute]: "" })}
    >
      <h2 className="border-b border-border/60 pb-1">
        <CollapsibleTrigger className="group -ms-2 flex h-7 items-center gap-1.5 rounded-md px-2 text-[13px] font-medium text-foreground outline-none hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-ring/60">
          <Caret />
          {title}
          {meta !== null ? (
            <span className={cn("ms-1 flex items-center gap-1.5 font-normal tabular-nums", QUIET)}>
              {meta}
            </span>
          ) : null}
        </CollapsibleTrigger>
      </h2>
      <CollapsiblePanel>{children}</CollapsiblePanel>
    </Collapsible>
  );
}

/** The small filled caret Linear puts before a group's title: down when open, right when closed. */
function Caret() {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden
      className={cn(
        "size-3 shrink-0 -rotate-90 transition-transform duration-150 group-data-[panel-open]:rotate-0 motion-reduce:transition-none",
        QUIET,
      )}
    >
      <path d="M3.5 6h9L8 11z" fill="currentColor" />
    </svg>
  );
}

/** Linear's progress ring and count: how many of the follow-ups are ticked off. */
function Progress({ done, total }: { readonly done: number; readonly total: number }) {
  const radius = 5.5;
  const length = 2 * Math.PI * radius;
  return (
    <>
      <svg viewBox="0 0 14 14" aria-hidden className="size-3.5 -rotate-90">
        <circle
          cx="7"
          cy="7"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.3}
          strokeWidth="1.5"
        />
        <circle
          cx="7"
          cy="7"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeDasharray={length}
          strokeDashoffset={length * (1 - (total === 0 ? 0 : done / total))}
          strokeLinecap="round"
        />
      </svg>
      {done}/{total}
    </>
  );
}

/** The moments' rows, or what stands in for them. */
function Findings({
  body,
  orgSegment,
  lineRecords,
  onRehearse,
  onTranscript,
  onAsk,
  retry,
}: {
  readonly body: ReviewPage["body"];
  readonly orgSegment: string;
  readonly lineRecords: ProbeReviewProps["lineRecords"];
  readonly onRehearse: (area: ProbeExaminationArea | null) => void;
  readonly onTranscript: (turnKey: string) => void;
  /** Put a question in the composer. */
  readonly onAsk: ((question: string) => void) | null;
  readonly retry: ProbeReviewProps["retry"];
}) {
  if (body.kind === "pending") {
    return (
      <p
        role="status"
        className={cn("flex h-9 items-center gap-2.5 px-2 text-[13px]", QUIET)}
        data-probe-review-pending=""
      >
        <span aria-hidden className="size-1.5 rounded-full bg-current motion-safe:animate-pulse" />
        Writing your review
      </p>
    );
  }
  if (body.kind === "failed") {
    return (
      <div
        className="flex min-h-9 flex-wrap items-center gap-x-3 gap-y-2 px-2 py-1"
        data-probe-review-failed=""
      >
        <p className={cn("text-[13px]", QUIET)}>The review couldn't be written.</p>
        {retry !== undefined ? (
          <Button
            size="xs"
            variant="outline"
            disabled={retry.busy}
            onClick={retry.run}
            data-probe-review-retry=""
          >
            <RotateCwIcon className={cn(retry.busy && "motion-safe:animate-spin")} />
            Retry
          </Button>
        ) : null}
      </div>
    );
  }
  if (body.kind === "debrief") {
    return (
      <ul className="flex flex-col py-1">
        {body.areas.map((area) => (
          <WorkOn key={area.area} area={area} onRehearse={onRehearse} />
        ))}
      </ul>
    );
  }
  if (body.moments.length === 0) {
    return (
      <p
        className="flex h-9 items-center px-2 text-[13px] text-foreground"
        data-probe-moments="none"
      >
        Every answer held up.
      </p>
    );
  }
  return (
    <ol className="flex flex-col py-1">
      {body.moments.map((moment, index) => (
        <Moment
          key={moment.turnKey}
          moment={moment}
          records={recordsInPlay(moment.turnKey, lineRecords)}
          orgSegment={orgSegment}
          defaultOpen={index === 0}
          onTranscript={onTranscript}
          onAsk={onAsk}
        />
      ))}
    </ol>
  );
}

/**
 * One moment as a compact row (an open status, its area, why it fell short
 * on one line, and an Ask about it) that opens in place: the whole reason,
 * the person's own words quoted with a link to their turn, the findings and
 * policies in play by then, and what a strong answer covers.
 */
function Moment({
  moment,
  records,
  orgSegment,
  defaultOpen,
  onTranscript,
  onAsk,
}: {
  readonly moment: ReviewMoment;
  readonly records: ReadonlyArray<DeskRecord>;
  readonly orgSegment: string;
  readonly defaultOpen: boolean;
  readonly onTranscript: (turnKey: string) => void;
  readonly onAsk: ((question: string) => void) | null;
}) {
  return (
    <Collapsible
      defaultOpen={defaultOpen}
      render={<li />}
      className="flex flex-col"
      data-probe-moment={moment.turnKey}
    >
      <div className="flex h-9 min-w-0 items-center gap-2 rounded-md hover:bg-foreground/[0.04]">
        <CollapsibleTrigger className="group flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-md px-2 text-left text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring/60">
          <span aria-hidden className="flex">
            <StatusIcon status="open" />
          </span>
          <span className="shrink-0 font-medium text-foreground">{areaLabel(moment.area)}</span>
          <span
            aria-hidden
            className={cn("min-w-0 flex-1 truncate group-data-[panel-open]:invisible", QUIET)}
          >
            {moment.reason}
          </span>
        </CollapsibleTrigger>
        {onAsk !== null ? (
          <Button
            size="xs"
            variant="outline"
            className="me-1 shrink-0"
            aria-label={`Ask about ${areaLabel(moment.area)}`}
            onClick={() => onAsk(momentQuestion(moment))}
            data-probe-moment-ask={moment.turnKey}
          >
            <MessageSquareTextIcon />
            Ask
          </Button>
        ) : null}
      </div>
      <CollapsiblePanel keepMounted>
        <div className="flex flex-col gap-3 pe-2 pt-0.5 pb-4 ps-[2.125rem] text-[13px] leading-5">
          <p className="text-pretty text-foreground" data-probe-reason="">
            {moment.reason}
          </p>
          <figure className="flex flex-col gap-1.5 border-s-2 border-border ps-3">
            <blockquote
              className="whitespace-pre-wrap text-pretty text-foreground/90"
              data-probe-said=""
            >
              {moment.said}
            </blockquote>
            <figcaption>
              <button
                type="button"
                onClick={() => onTranscript(moment.turnKey)}
                className={cn(
                  "cursor-pointer rounded-sm text-xs tabular-nums underline-offset-[3px] outline-none hover:text-foreground hover:underline focus-visible:ring-2 focus-visible:ring-ring/60",
                  QUIET,
                )}
                aria-label="You, in the transcript"
                data-probe-to-turn={moment.turnKey}
              >
                You
              </button>
            </figcaption>
          </figure>
          {records.length > 0 ? (
            <div className="flex flex-col gap-1" data-probe-in-play="">
              <p className={cn("text-xs font-medium", QUIET)}>In play</p>
              <ProbeRecordChips records={records} orgSegment={orgSegment} className="mt-0" />
            </div>
          ) : null}
          <div className="flex flex-col gap-1">
            <p className={cn("text-xs font-medium", QUIET)}>A strong answer covers</p>
            <p className="text-pretty text-foreground" data-probe-stronger="">
              {moment.strongerAnswer}
            </p>
          </div>
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
}

/** The areas the debrief rated, as chips beside the properties: each its glyph and whole name, opening Home on that area. */
function Areas({
  areas,
  orgSegment,
}: {
  readonly areas: ReadonlyArray<RatedArea>;
  readonly orgSegment: string;
}) {
  return (
    <ul aria-label="Areas" className="contents" data-probe-areas="">
      {areas.map((area) => (
        <li key={area.area} className="flex" data-probe-area={area.area}>
          <Link
            to="/$org/apps/probe/practice"
            params={{ org: orgSegment }}
            search={{ area: areaSlug(area.area) }}
            title={area.note ?? undefined}
            className="ms-1 inline-flex min-h-6 max-w-full items-center gap-1.5 rounded-full border border-border/70 py-0.5 ps-1.5 pe-2.5 text-xs leading-4 text-foreground outline-none hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-ring/60"
          >
            <AssessmentGlyph assessment={area.assessment} />
            <span className="text-pretty">{areaLabel(area.area)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** An area the debrief rated weak or adequate, on an interview with no review. */
function WorkOn({
  area,
  onRehearse,
}: {
  readonly area: ReviewArea;
  readonly onRehearse: (area: ProbeExaminationArea | null) => void;
}) {
  const id = useId();
  return (
    <li
      className="flex items-start gap-3 rounded-md px-2 py-2 text-[13px] leading-5"
      data-probe-work-on={area.area}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 id={id} className="font-medium text-foreground">
          {areaLabel(area.area)}
        </h3>
        {area.note !== null ? <p className={cn("text-pretty", QUIET)}>{area.note}</p> : null}
      </div>
      <Button
        size="xs"
        variant="outline"
        aria-describedby={id}
        onClick={() => onRehearse(area.area)}
        data-probe-rehearse={area.area}
      >
        <TargetIcon />
        <span className="max-sm:sr-only">Rehearse again</span>
      </Button>
    </li>
  );
}

/** The actions as one checklist, keeping its ticks on this device. */
function Actions({
  interviewId,
  actions,
}: {
  readonly interviewId: string;
  readonly actions: ReadonlyArray<string>;
}) {
  const isDone = useActionsDone();
  if (actions.length === 0) return null;
  return (
    <ReviewGroup
      title="Actions"
      meta={
        <Progress
          done={actions.filter((action) => isDone(interviewId, action)).length}
          total={actions.length}
        />
      }
      dataAttribute="data-probe-actions"
    >
      <ul className="flex flex-col py-1">
        {actions.map((action) => {
          const checked = isDone(interviewId, action);
          return (
            <li key={action}>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md px-2 py-1.5 text-[13px] leading-5 hover:bg-foreground/[0.04]">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(next) => setActionDone(interviewId, action, next === true)}
                  className="mt-0.5"
                  data-probe-action=""
                />
                <span
                  className={cn(
                    "min-w-0 flex-1 text-pretty text-foreground",
                    checked && "text-muted-foreground line-through decoration-muted-foreground/60",
                  )}
                >
                  {action}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </ReviewGroup>
  );
}
